import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useApiQuery } from '../../hooks/useApi';
import { toast } from 'react-hot-toast';
import { ReporteProvider, useReporte, reporteActions } from '../../context/ReporteContext';
import FormularioReporteMovil from '../../components/reportes/FormularioReporteMovil';
import { ReportesService } from '../../services';
import { ApiClient } from '../../services/apiClient';

/**
 * Vista móvil para editar un reporte existente
 * Sigue el mismo patrón que desktop: provider vacío + useEffect para cargar datos
 */

// Componente externo que provee el contexto
const EditarReporteMovilView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const reporteId = id ? parseInt(id, 10) : null;

  if (!reporteId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-red-600">ID de reporte inválido</p>
        </div>
      </div>
    );
  }

  return (
    <ReporteProvider>
      <EditarReporteMovilContent reporteId={reporteId} />
    </ReporteProvider>
  );
};

// Componente interno que usa el contexto (IGUAL QUE DESKTOP)
interface EditarReporteMovilContentProps {
  reporteId: number;
}

const EditarReporteMovilContent: React.FC<EditarReporteMovilContentProps> = ({ reporteId }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { dispatch } = useReporte();
  const [datosPreCargados, setDatosPreCargados] = useState(false);

  // Cargar reporte desde API (IGUAL QUE DESKTOP)
  const {
    data: reporte,
    isLoading,
    error,
    refetch,
  } = useApiQuery(
    ['reporte-completo', reporteId],
    () => reporteId ? ReportesService.getReporte(reporteId) : Promise.resolve(null),
    {
      enabled: !!reporteId,
      staleTime: 0,
      cacheTime: 0,
    }
  );

  useEffect(() => {
    if (error) {
      toast.error('Error al cargar el reporte');
      navigate('/reportes');
    }
  }, [error, navigate]);

  // Cargar archivos KML asociados a cada faena (CORREGIDO para usar datos del backend)
  const cargarArchivosKML = async (faena: any, _reporteId: number): Promise<any[]> => {
    try {
      // ✅ OPTIMIZADO: Usar archivos_kml que ya vienen del backend en lugar de hacer llamada adicional
      const archivosBase = faena.archivos_kml || [];

      if (archivosBase.length === 0) {
        return []; // No hay KMLs para esta faena
      }

      // Cargar polígonos de cada archivo
      const archivosConPoligonos = await Promise.all(
        archivosBase.map(async (archivo: any) => {
          try {
            const poligonosResponse = await ApiClient.get(`/kml/${archivo.id}/poligonos`);
            const poligonos = poligonosResponse as any;

            return {
              id: archivo.id,
              archivo_id: archivo.id,
              nombre_original: archivo.nombre_archivo_original,
              area_hectareas: archivo.area_hectareas,
              longitud_metros: archivo.longitud_total_metros || 0,
              file: null,
              processed: true,
              poligonos: poligonos.map((poligono: any) => ({
                id: poligono.id,
                nombre: poligono.nombre,
                descripcion: poligono.descripcion,
                area_hectareas: poligono.area_hectareas,
                coordinates: [],
                orden: poligono.orden
              }))
            };
          } catch (error) {
            return {
              id: archivo.id,
              archivo_id: archivo.id,
              nombre_original: archivo.nombre_archivo_original,
              area_hectareas: archivo.area_hectareas,
              longitud_metros: archivo.longitud_total_metros || 0,
              file: null,
              processed: true,
              poligonos: []
            };
          }
        })
      );

      return archivosConPoligonos;
    } catch (error) {
      console.error(`Error procesando archivos KML para ReporteFaena ${faena.id}:`, error);
      return [];
    }
  };

  // Efecto para pre-cargar datos del reporte en el contexto (IGUAL QUE DESKTOP)
  useEffect(() => {
    if (reporte && !datosPreCargados) {
      const transformarReporteParaContexto = async () => {
        try {
          // Transformar faenas
          const faenasTransformadas = await Promise.all(
            reporte.faenas.map(async (faena: any, index: number) => {
              const archivosKml = await cargarArchivosKML(faena, reporteId);

              return {
                id_temporal: `faena_${faena.id}_${Date.now()}_${index}`,
                faena_id: faena.faena_id,
                predio_id: faena.predio_id || null,
                rodal_id: faena.rodal_id || null,
                horas: faena.horas,
                area_trabajada: faena.area || 0,
                unidad_medida: 'ha',
                descripcion: faena.faena_descripcion || `Faena ${faena.faena_id}`,
                faena_codigo: faena.faena_codigo || '',
                predio_codigo: faena.predio_codigo || '',
                rodal_codigo: faena.rodal_codigo || '',
                archivos_kml: archivosKml,
                area_kml_total: archivosKml.reduce((total: number, kml: any) => total + (kml.area_hectareas || 0), 0),
                longitud_kml_total: archivosKml.reduce((total: number, kml: any) => total + (kml.longitud_metros || 0), 0),
                calificadores: (faena.calificadores || []).map((c: any) => ({ calificador_id: c.calificador_id, valor: c.valor })),
                observaciones: '',
              };
            })
          );

          // Transformar insumos
          const insumosTransformados = reporte.insumos.map((insumo: any, index: number) => ({
            id_temporal: `insumo_${insumo.id}_${Date.now()}_${index}`,
            insumo_id: insumo.insumo_id,
            nombre_insumo: insumo.insumo_descripcion || `Insumo ${insumo.insumo_id}`,
            cantidad: insumo.cantidad,
            unidad: insumo.unidad_medida || 'kg',
            costo_unitario: 0,
          }));

          // Transformar items de chequeo
          const itemsChequeoTransformados = reporte.items_chequeo.map((item: any, index: number) => ({
            id_temporal: `item_${item.id}_${Date.now()}_${index}`,
            item_chequeo_id: item.itemchequeo_id,
            nombre_item: item.item_descripcion || `Item ${item.itemchequeo_id}`,  // ✅ CORREGIDO: Usar campo plano
            verificado: true,
            horometro: item.horometro,
            tiempo: item.tiempo,
            observaciones: item.observaciones || '',
            requiere_atencion: false,  // ✅ SIMPLIFICADO: El backend no envía este campo en el response
          }));

          // Cargar todos los datos en el contexto usando inicializar
          dispatch(reporteActions.inicializar({
            numero_reporte: reporte.numero,
            maquina_id: reporte.maquina_id,
            maquina_info: {
              codigo: reporte.maquina_codigo || '',
              tipo: '',
              descripcion: reporte.maquina_descripcion || '',
            },
            fecha_reporte: reporte.fecha.split('T')[0],
            operador_id: reporte.operador_id || null,
            operador_info: reporte.operador_id ? {
              codigo: reporte.operador_codigo || '',
              nombre: reporte.operador_descripcion || '',
            } : undefined,
            horometro_inicial: reporte.horometro_inicial,
            horometro_final: reporte.horometro_final,
            horas_trabajadas: reporte.horometro_final - reporte.horometro_inicial,
            faenas: faenasTransformadas,
            insumos: insumosTransformados,
            items_chequeo: itemsChequeoTransformados,
            estado_maquina: reporte.estado_maquina || 'OPERATIVA',
            observaciones: reporte.observaciones || '',
            novedades: reporte.novedades || '',
            estado_reporte: 'completo',
          }));

          setDatosPreCargados(true);
          toast.success('Reporte cargado para edición');

        } catch (error) {
          console.error('Error transformando reporte:', error);
          toast.error('Error al cargar datos del reporte');
        }
      };

      transformarReporteParaContexto();
    }
  }, [reporte, datosPreCargados, dispatch, reporteId]);

  // Limpiar contexto al salir
  useEffect(() => {
    return () => {
      if (datosPreCargados) {
        dispatch(reporteActions.limpiar());
      }
    };
  }, [datosPreCargados, dispatch]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!reporte) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-red-600">{t('errors.not_found')}</p>
        </div>
      </div>
    );
  }

  return (
    <FormularioReporteMovil
      modoEdicion={true}
      reporteId={reporteId}
      refetchReporte={refetch}
    />
  );
};

export default EditarReporteMovilView;
